/**
 * Sticky bulk-action strip — appears above the table once one or more
 * rows are checked. Lets the user reclassify, archive, or ignore the
 * selection in a single API call.
 *
 * Designed to *float* visually above the table without pushing layout
 * around. Only an "Mark as ▾" + "Archive" + "Ignore" + "Clear" is shown
 * — anything more dangerous (delete, etc.) stays in the per-row menu.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Archive, ArchiveRestore, EyeOff, Eye, Loader2, Tag, X,
} from 'lucide-react';

const TYPES = [
  { value: 'employee',          label: 'Employee' },
  { value: 'non_employee',      label: 'Non-Employee' },
  { value: 'distribution_list', label: 'Distribution List' },
];

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export default function BulkActionBar({
  selectedEmails,    // string[]
  onClear,
  onAfterMutate,     // () => void  — parent refresh hook
}) {
  const [pendingType, setPendingType] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const count = selectedEmails?.length || 0;
  if (!count) return null;

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const callBulk = async (endpoint, body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/employees/bulk/${endpoint}`, body, {
        headers: authHeader(), timeout: 30000,
      });
      const { applied_count, skipped_count } = r.data || {};
      toast.success(
        `${applied_count || 0} updated` +
        (skipped_count ? ` · ${skipped_count} skipped` : '')
      );
      onAfterMutate && onAfterMutate();
      onClear && onClear();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Bulk ${endpoint} failed`);
    } finally {
      setBusy(false);
    }
  };

  const submitReclassify = async () => {
    if (!pendingType) return;
    setReasonOpen(false);
    await callBulk('classify', {
      emails: selectedEmails,
      identity_type: pendingType,
      reason: reason.trim() || undefined,
    });
    setPendingType(null);
    setReason('');
  };

  return (
    <>
      <div
        className="sticky top-2 z-20 mb-3 mx-auto max-w-3xl"
        data-testid="bulk-action-bar"
      >
        <div className="flex items-center justify-between gap-3 rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 shadow-sm">
          <span className="text-sm font-medium text-emerald-900">
            <span data-testid="bulk-selection-count">{count}</span> selected
          </span>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-300 bg-white"
                  disabled={busy}
                  data-testid="bulk-mark-as-trigger"
                >
                  <Tag className="w-4 h-4 mr-1.5" />
                  Mark as
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Set identity type</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {TYPES.map((t) => (
                  <DropdownMenuItem
                    key={t.value}
                    onClick={() => { setPendingType(t.value); setReasonOpen(true); }}
                    data-testid={`bulk-mark-as-${t.value}`}
                  >
                    {t.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="sm"
              className="border-emerald-300 bg-white"
              disabled={busy}
              onClick={() => callBulk('archive', { emails: selectedEmails })}
              data-testid="bulk-archive-btn"
            >
              <Archive className="w-4 h-4 mr-1.5" /> Archive
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-emerald-300 bg-white"
              disabled={busy}
              onClick={() => callBulk('unarchive', { emails: selectedEmails })}
              data-testid="bulk-unarchive-btn"
            >
              <ArchiveRestore className="w-4 h-4 mr-1.5" /> Unarchive
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              disabled={busy}
              data-testid="bulk-cancel-btn"
              className="text-emerald-900 hover:bg-emerald-100"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Reason dialog — appears after picking a type. Reason optional. */}
      <Dialog open={reasonOpen} onOpenChange={(v) => { if (!v) { setReasonOpen(false); setPendingType(null); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-reason-dialog">
          <DialogHeader>
            <DialogTitle>
              Mark {count} as {TYPES.find((t) => t.value === pendingType)?.label}
            </DialogTitle>
            <DialogDescription>
              Add an optional reason for the audit trail. Leave blank to skip.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Q1 vendor onboarding"
            data-testid="bulk-reason-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setReasonOpen(false); setPendingType(null); }} data-testid="bulk-reason-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitReclassify}
              disabled={busy}
              data-testid="bulk-reason-submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Apply to {count}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
